import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MapPin, Globe, Instagram, MessageCircle } from 'lucide-react'
import { STRIPE } from '@/data/mockData'
import { mapPostRow } from '@/lib/mapPost'
import PostCard from '@/components/features/feed/PostCard'
import { useAuthStore } from '@/store/auth.store'
import { useBusinessProfile, useMyFollows, useToggleFollow } from '@/hooks/useBusiness'
import { toast } from '@/components/ui/Toast'

const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

interface PostRow {
  id: string
  created_at: string
  is_active: boolean
  [key: string]: unknown
}

interface BusinessProfile {
  id: string
  name: string
  category: string
  zone: string
  description: string | null
  verified: boolean
  cover_colors: [string, string]
  emoji: string
  address: string | null
  whatsapp: string | null
  instagram: string | null
  website: string | null
  is_open: boolean
  follower_count: number
  posts: PostRow[]
}

export default function BizProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const { data, isLoading } = useBusinessProfile(id)
  const business = data?.data as unknown as BusinessProfile | undefined

  const myFollows = useMyFollows()
  const isFollowing = myFollows.has(id ?? '')
  const toggleFollow = useToggleFollow(id ?? '')
  const [following, setFollowing] = useState(false)

  useEffect(() => setFollowing(isFollowing), [isFollowing])

  const requireAuth = (action: string) => {
    if (!user) {
      toast.info(`Inicia sesión para ${action}`)
      return false
    }
    return true
  }

  if (isLoading) {
    return (
      <div className="page-sm">
        <div
          style={{
            height: 180,
            borderRadius: 20,
            marginBottom: 24,
            background: C.grayBg,
            animation: 'pxPulse 1.5s infinite',
          }}
        />
      </div>
    )
  }

  if (!business) {
    return (
      <div className="page-sm" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
          No encontramos este negocio
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 22px',
            borderRadius: 12,
            border: 'none',
            background: C.teal,
            color: '#fff',
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Volver al inicio
        </button>
      </div>
    )
  }

  const posts = (business.posts ?? []).map((row) => ({ row, post: mapPostRow(row, business) }))

  return (
    <div className="page-sm">
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: C.ink2,
          fontFamily: FB,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <ArrowLeft size={18} /> Volver
      </button>

      {/* Portada */}
      <div
        style={{
          height: 120,
          borderRadius: '20px 20px 0 0',
          background: STRIPE(business.cover_colors[0], business.cover_colors[1]),
        }}
      />

      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderTop: 'none',
          borderRadius: '0 0 20px 20px',
          padding: '0 24px 24px',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 16,
            marginTop: -36,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 18,
              background: STRIPE(business.cover_colors[0], business.cover_colors[1]),
              border: '4px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 34,
              flexShrink: 0,
            }}
          >
            {business.emoji}
          </div>
          <button
            onClick={() => {
              if (!requireAuth('seguir a este negocio')) return
              const next = !following
              setFollowing(next)
              toggleFollow.mutate(next, { onError: () => setFollowing(!next) })
            }}
            style={{
              marginLeft: 'auto',
              marginBottom: 6,
              padding: '10px 20px',
              borderRadius: 11,
              border: `1px solid ${following ? C.terra : C.teal}`,
              background: following ? C.terra : C.teal,
              color: '#fff',
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {following ? 'Dejar de seguir' : 'Seguir'}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontFamily: FH, fontWeight: 800, fontSize: 22 }}>{business.name}</span>
          {business.verified && <span style={{ color: C.teal, fontSize: 16 }}>✓</span>}
          <span
            style={{
              marginLeft: 6,
              fontSize: 12,
              fontWeight: 700,
              padding: '3px 9px',
              borderRadius: 99,
              background: business.is_open ? `${C.teal}18` : `${C.ink2}18`,
              color: business.is_open ? C.teal : C.ink2,
            }}
          >
            {business.is_open ? 'Abierto' : 'Cerrado'}
          </span>
        </div>

        <div
          style={{
            fontSize: 14,
            color: C.ink2,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginBottom: 14,
          }}
        >
          {business.category} · {business.zone}
        </div>

        <div style={{ display: 'flex', gap: 20, marginBottom: business.description ? 14 : 0 }}>
          <div>
            <span style={{ fontFamily: FH, fontWeight: 800, fontSize: 16 }}>
              {business.follower_count}
            </span>{' '}
            <span style={{ fontSize: 13, color: C.ink2 }}>seguidores</span>
          </div>
          <div>
            <span style={{ fontFamily: FH, fontWeight: 800, fontSize: 16 }}>{posts.length}</span>{' '}
            <span style={{ fontSize: 13, color: C.ink2 }}>publicaciones</span>
          </div>
        </div>

        {business.description && (
          <p style={{ fontSize: 14.5, color: C.ink, lineHeight: 1.55, marginBottom: 14 }}>
            {business.description}
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {business.address && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: C.ink2,
              }}
            >
              <MapPin size={14} /> {business.address}
            </span>
          )}
          {business.whatsapp && (
            <a
              href={`https://wa.me/${business.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: C.teal,
                textDecoration: 'none',
              }}
            >
              <MessageCircle size={14} /> WhatsApp
            </a>
          )}
          {business.instagram && (
            <a
              href={`https://instagram.com/${business.instagram.replace(/^@/, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: C.teal,
                textDecoration: 'none',
              }}
            >
              <Instagram size={14} /> Instagram
            </a>
          )}
          {business.website && (
            <a
              href={business.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: C.teal,
                textDecoration: 'none',
              }}
            >
              <Globe size={14} /> Sitio web
            </a>
          )}
        </div>
      </div>

      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 17, marginBottom: 14 }}>
        Publicaciones
      </div>

      {posts.length === 0 ? (
        <div
          style={{
            padding: '18px 20px',
            borderRadius: 12,
            background: '#fff',
            border: `1px solid ${C.border}`,
            color: C.ink2,
            fontSize: 13.5,
          }}
        >
          Este negocio aún no ha publicado nada.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
            gap: 16,
          }}
        >
          {posts.map(({ row, post }) => (
            <div key={post.id} style={{ opacity: row.is_active ? 1 : 0.6, position: 'relative' }}>
              {!row.is_active && (
                <span
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 1,
                    background: 'rgba(0,0,0,.55)',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '4px 9px',
                    borderRadius: 8,
                  }}
                >
                  Finalizada
                </span>
              )}
              <PostCard post={post} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
